import { useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { Bedarf, BedarfPrioritaetConfig, calculateTotalHours, formatHours } from '../../types/bedarf';
import { BedarfPriorityBadge } from './BedarfPriorityBadge';
import { BedarfActionDropdown } from './actions';
import { UserAvatar } from '../UserAvatar';
import { useDetailNavigation } from '../../hooks/useDetailNavigation';

interface KanbanCardProps {
  bedarf: Bedarf;
  isDragging?: boolean;
}

export function KanbanCard({ bedarf, isDragging }: KanbanCardProps) {
  const detailProps = useDetailNavigation();
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
  } = useSortable({
    id: bedarf.id,
    data: {
      type: 'bedarf',
      bedarf,
    },
  });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
  };

  const priorityConfig = BedarfPrioritaetConfig[bedarf.priority];

  return (
    <div
      ref={setNodeRef}
      style={style}
      {...attributes}
      {...listeners}
      {...(isDragging ? {} : detailProps(`/bedarfe/${bedarf.id}`))}
      className={`
        bg-white rounded-lg shadow-sm border border-gray-200 p-3 mb-2
        cursor-grab active:cursor-grabbing
        hover:shadow-md hover:border-gray-300 transition-all
        ${isDragging ? 'opacity-50 shadow-lg ring-2 ring-primary-500' : ''}
      `}
    >
      {/* Header with child initials, priority, and actions */}
      <div className="flex items-center justify-between mb-2">
        <div className="flex items-center min-w-0">
          <div className={`h-8 w-8 rounded-full ${priorityConfig.bgColor} flex items-center justify-center mr-2 flex-shrink-0`}>
            <span className={`font-medium text-xs ${priorityConfig.color}`}>
              {bedarf.childFirstName[0]}{bedarf.childLastName?.[0] || ''}
            </span>
          </div>
          <div className="min-w-0 flex-1">
            <p className="text-sm font-medium text-gray-900 truncate">
              {bedarf.childFirstName}{bedarf.childLastName ? ` ${bedarf.childLastName}` : ''}
            </p>
          </div>
        </div>
        <div className="flex items-center space-x-1 flex-shrink-0">
          <BedarfPriorityBadge priority={bedarf.priority} size="sm" />
          <div
            onClick={(e) => e.stopPropagation()}
            onAuxClick={(e) => e.stopPropagation()}
            onPointerDown={(e) => e.stopPropagation()}
            onMouseDown={(e) => e.stopPropagation()}
          >
            <BedarfActionDropdown bedarf={bedarf} size="sm" align="right" />
          </div>
        </div>
      </div>

      {/* Details */}
      <div className="space-y-1 text-xs text-gray-500">
        {bedarf.schoolName && (
          <div className="flex items-center">
            <svg className="h-3 w-3 mr-1 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
            </svg>
            <span className="truncate">{bedarf.schoolName}</span>
          </div>
        )}
        {bedarf.schoolCity && (
          <div className="flex items-center">
            <svg className="h-3 w-3 mr-1 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
            </svg>
            <span className="truncate">{bedarf.schoolCity.name}</span>
          </div>
        )}
        {calculateTotalHours(bedarf) > 0 && (
          <div className="flex items-center">
            <svg className="h-3 w-3 mr-1 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
            <span>{formatHours(calculateTotalHours(bedarf))}/Woche</span>
          </div>
        )}
      </div>

      {/* Footer */}
      <div className="mt-2 pt-2 border-t border-gray-100 flex items-center justify-between">
        <div className="flex items-center text-xs text-gray-400">
          {bedarf.startDateRequested && (
            <span>
              Ab {new Date(bedarf.startDateRequested).toLocaleDateString('de-DE', { day: '2-digit', month: '2-digit' })}
            </span>
          )}
        </div>
        <div className="flex items-center gap-2">
          {bedarf.assignedTo && (
            <UserAvatar user={bedarf.assignedTo} size="xs" />
          )}
          <div className="text-xs text-gray-400">
            {new Date(bedarf.updatedAt).toLocaleDateString('de-DE', { day: '2-digit', month: '2-digit' })}
          </div>
        </div>
      </div>
    </div>
  );
}

// Overlay version for drag preview
export function KanbanCardOverlay({ bedarf }: { bedarf: Bedarf }) {
  const priorityConfig = BedarfPrioritaetConfig[bedarf.priority];

  return (
    <div className="bg-white rounded-lg shadow-xl border-2 border-primary-500 p-3 w-56 rotate-3">
      <div className="flex items-center mb-2">
        <div className={`h-8 w-8 rounded-full ${priorityConfig.bgColor} flex items-center justify-center mr-2`}>
          <span className={`font-medium text-xs ${priorityConfig.color}`}>
            {bedarf.childFirstName[0]}{bedarf.childLastName?.[0] || ''}
          </span>
        </div>
        <p className="text-sm font-medium text-gray-900 truncate">
          {bedarf.childFirstName}{bedarf.childLastName ? ` ${bedarf.childLastName}` : ''}
        </p>
      </div>
      {bedarf.schoolName && (
        <p className="text-xs text-gray-500">{bedarf.schoolName}</p>
      )}
    </div>
  );
}
